import {
  PropertyPortal,
  type PropertyPortalPublication,
} from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  getMeliPublicationProviderStatus,
  getMeliBillingPause,
  isMeliPublicationBillingBlocked,
  isMeliPublicationPaymentRequired,
} from './meli-publication.util';

export interface PropertyPublicationAlert {
  code: string;
  descriptionKey: TranslationKey;
  severity: 'info' | 'warning' | 'danger';
  summaryKey: TranslationKey;
  titleKey: TranslationKey;
}

export const getPublicationAlerts = (
  publication: PropertyPortalPublication | null
): PropertyPublicationAlert[] => {
  if (!publication) {
    return [];
  }

  switch (publication.portal) {
    case PropertyPortal.MELI:
      return getMeliPublicationAlerts(publication);
    case PropertyPortal.TOCTOC:
    case PropertyPortal.PROPPIT:
    case PropertyPortal.YAPO:
      return [];
  }
};

export const getPrimaryPublicationAlert = (
  publication: PropertyPortalPublication | null
): PropertyPublicationAlert | null => {
  return getPublicationAlerts(publication)[0] ?? null;
};

const getMeliPublicationAlerts = (
  publication: PropertyPortalPublication
): PropertyPublicationAlert[] => {
  const alerts: PropertyPublicationAlert[] = [];

  const billingAlert = isMeliPublicationBillingBlocked(publication)
    ? 'billingBlocked'
    : getMeliBillingPause(publication) === 'after_billing'
      ? 'billingPauseContinued'
      : null;
  if (billingAlert) {
    alerts.push({
      code: billingAlert,
      severity: 'warning',
      titleKey: `catalog.properties.publicationAlerts.${billingAlert}.title`,
      descriptionKey: `catalog.properties.publicationAlerts.${billingAlert}.description`,
      summaryKey: `catalog.properties.publicationAlerts.${billingAlert}.summary`,
    });
  }

  if (
    isMeliPublicationPaymentRequired(publication) ||
    getMeliPublicationProviderStatus(publication) === 'payment_required'
  ) {
    alerts.push({
      code: 'payment_required',
      severity: 'warning',
      titleKey: 'catalog.properties.publicationAlerts.paymentRequired.title',
      descriptionKey:
        'catalog.properties.publicationAlerts.paymentRequired.description',
      summaryKey:
        'catalog.properties.publicationAlerts.paymentRequired.summary',
    });
  }

  if (getMeliPublicationProviderStatus(publication) === 'under_review') {
    alerts.push({
      code: 'under_review',
      severity: 'warning',
      titleKey: 'catalog.properties.publicationAlerts.underReview.title',
      descriptionKey:
        'catalog.properties.publicationAlerts.underReview.description',
      summaryKey: 'catalog.properties.publicationAlerts.underReview.summary',
    });
  }

  return alerts;
};
